import { useLazyQuery } from '@apollo/client';
import { ResultOf } from '@graphql-typed-document-node/core';
import debounce from 'lodash/debounce';
import { FC, useEffect, useMemo, useState } from 'react';
import { useMount } from 'react-use';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  Icon,
  LoadingIcon,
  Text,
  VStack,
  useComboboxInputValue,
  useSetComboboxInputValue,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

// Operations

const promptTemplateOptionFragment = graphql(`
  fragment PromptTemplateOption on PromptTemplates @_unmask {
    __typename
    id
    name
    description
  }
`);

const searchPromptTemplatesQuery = graphql(
  `
    query searchPromptTemplatesForSelector(
      $filter: PromptTemplatesBoolExp
      $limit: Int
    ) {
      promptTemplates(where: $filter, limit: $limit, orderBy: { name: ASC }) {
        ...PromptTemplateOption
      }
    }
  `,
  [promptTemplateOptionFragment],
);

const getDefaultPromptTemplateQuery = graphql(
  `
    query getDefaultDynamicOutputTemplate {
      promptTemplates(
        where: {
          _and: [
            { outputType: { _eq: DYNAMIC_OUTPUT } }
            { isActive: { _eq: true } }
            { isCurrentDefault: { _eq: true } }
          ]
        }
        limit: 1
      ) {
        ...PromptTemplateOption
      }
    }
  `,
  [promptTemplateOptionFragment],
);

// Private Components

const PromptTemplateSearchResults: FC<{
  onTemplateSelected?: (
    template: ResultOf<typeof promptTemplateOptionFragment>,
  ) => void;
}> = ({ onTemplateSelected }) => {
  const searchValue = useComboboxInputValue();
  const setSearchValue = useSetComboboxInputValue();
  const [searchResults, setSearchResults] = useState<
    ResultOf<typeof promptTemplateOptionFragment>[]
  >([]);
  const [searchTemplates, { loading }] = useLazyQuery(
    searchPromptTemplatesQuery,
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (!query || query.length < 1) {
          setSearchResults([]);
          return;
        }

        const filter = {
          _and: [
            { outputType: { _eq: 'DYNAMIC_OUTPUT' as const } },
            { isActive: { _eq: true } },
            { name: { _ilike: `%${query}%` } },
          ],
        };

        searchTemplates({
          variables: { filter, limit: 25 },
        }).then((result) => {
          const templates = result.data?.promptTemplates ?? [];
          setSearchResults(templates);
        });
      }, 250),
    [searchTemplates],
  );

  useEffect(() => {
    debouncedSearch(searchValue);
  }, [searchValue, debouncedSearch]);

  useEffect(
    function cancelDebounceOnUnmount() {
      return () => debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  if (!searchValue || searchValue.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <ComboboxEmpty>
        <LoadingIcon className="mx-auto text-text-supporting" size="sm" />
      </ComboboxEmpty>
    );
  }

  if (searchResults.length === 0) {
    return (
      <ComboboxEmpty>
        <Text size="md" color="supporting">
          No matching templates found.
        </Text>
      </ComboboxEmpty>
    );
  }

  return searchResults.map((template) => (
    <ComboboxOption
      key={template.id}
      onSelect={() => {
        setSearchValue('');
        onTemplateSelected?.(template);
      }}
    >
      <VStack className="p-1" gap={1}>
        <Text size="md">{template.name}</Text>
        {template.description && (
          <Text size="sm" color="secondary">
            {template.description}
          </Text>
        )}
      </VStack>
    </ComboboxOption>
  ));
};

// Public Components

interface PromptTemplateSelectorProps {
  disabled?: boolean;
  value?: string;
  onTemplateSelected?: (templateId?: string) => void;
}

export const PromptTemplateSelector: FC<PromptTemplateSelectorProps> = ({
  disabled = false,
  value,
  onTemplateSelected,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<ResultOf<
    typeof promptTemplateOptionFragment
  > | null>(null);
  const [isLoadingDefault, setIsLoadingDefault] = useState(false);
  const [searchTemplates] = useLazyQuery(searchPromptTemplatesQuery);
  const [getDefaultTemplate] = useLazyQuery(getDefaultPromptTemplateQuery);

  // Load default template on mount if no value is provided
  useMount(() => {
    if (!value) {
      setIsLoadingDefault(true);
      getDefaultTemplate()
        .then((result) => {
          const defaultTemplate = result.data?.promptTemplates?.[0] ?? null;
          if (defaultTemplate) {
            setSelectedTemplate(defaultTemplate);
            onTemplateSelected?.(defaultTemplate.id);
          }
          setIsLoadingDefault(false);
        })
        .catch(() => {
          setIsLoadingDefault(false);
        });
    }
  });

  // Fetch template data when value is provided but no selected template exists
  useEffect(() => {
    if (!value || selectedTemplate?.id === value) {
      return;
    }

    searchTemplates({
      variables: { filter: { id: { _eq: value } }, limit: 1 },
    }).then((result) => {
      const template = result.data?.promptTemplates?.[0] ?? null;
      setSelectedTemplate(template);
    });
  }, [value, selectedTemplate?.id, searchTemplates]);

  const handleTemplateSelected = (
    template: ResultOf<typeof promptTemplateOptionFragment>,
  ) => {
    setSelectedTemplate(template);
    onTemplateSelected?.(template.id);
  };

  return (
    <Combobox disabled={disabled || isLoadingDefault}>
      <ComboboxSelectButton
        className="h-10 w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm hover:border-border-secondary"
        dropdownIcon={
          isLoadingDefault ? (
            <LoadingIcon className="shrink-0 opacity-50" size="xs" />
          ) : (
            <Icon
              name="ChevronDown"
              className="shrink-0 opacity-50"
              size="xs"
            />
          )
        }
      >
        {selectedTemplate ? (
          <span>{selectedTemplate.name}</span>
        ) : (
          <span className="text-text-tertiary">
            {isLoadingDefault
              ? 'Loading default template...'
              : 'Select a template'}
          </span>
        )}
      </ComboboxSelectButton>
      <ComboboxDropdown
        searchPlaceholder="Search prompt templates..."
        shouldFilter={false}
      >
        <PromptTemplateSearchResults
          onTemplateSelected={handleTemplateSelected}
        />
      </ComboboxDropdown>
    </Combobox>
  );
};
